import { clsx } from "clsx";
import { Link } from "react-router-dom";
import { SectionCard, StatTile, TableShell, Th } from "@/features/power_projects/components/ui";
import { SIDE_LABEL, type AnalysisAttention, type AnalysisProof, type AnalysisResult } from "../types";
import { TEXT_WARN, fmtDate } from "./common";

const DONE: { key: string; label: string }[] = [
  { key: "titles_secured", label: "tytuły pozyskane" },
  { key: "titles_confirmed", label: "potwierdzone przez nas" },
  { key: "tiles_moved", label: "kafle ruszone" },
  { key: "letters_out", label: "pisma wysłane" },
  { key: "letters_in", label: "odpowiedzi" },
  { key: "entries", label: "wpisy działań" },
];
const RISK: { key: string; label: string }[] = [
  { key: "titles_pending", label: "czekają na nas" },
  { key: "letters_overdue", label: "pisma po terminie" },
  { key: "parcels_stalled", label: "działki bez ruchu" },
  { key: "reports_overdue", label: "raporty po terminie" },
  { key: "memos_unread", label: "notatki bez odczytu" },
];
const PROOF_PREFIX: Record<AnalysisProof["kind"], string> = { title_secured: "Tytuł pozyskany:", tile: "Kafel:", letter: "Pismo:" };
const ATTENTION_PREFIX: Record<AnalysisAttention["kind"], string> = {
  letter_overdue: "Pismo po terminie:", parcels_stalled: "Działki bez ruchu:", memo_unread: "Notatka bez odczytu:", tile_overdue: "Kafel po terminie:",
  report_overdue: "Raport po terminie:", waiting_on_us: "Czeka na nas:", project_idle: "Projekt bez ruchu:",
};

function Entry({ tone, prefix, main, details }: { tone: "ok" | "bad" | "warn"; prefix: string; main: string; details: React.ReactNode }) {
  return (
    <li className={clsx("border-l-[3px] pl-2.5", tone === "ok" ? "border-emerald-600" : tone === "warn" ? "border-amber-500" : "border-[var(--color-primary)]")}>
      <div className="text-xs text-[var(--color-text-primary)]"><span className="font-semibold">{prefix}</span> {main}</div>
      <div className="font-mono text-[11px] text-[var(--color-text-muted)]">{details}</div>
    </li>
  );
}

/** Rozliczenie firmy w oknie: co zrobiła (dowody), co wymaga uwagi, kafle przed/po.
 *  Ten sam panel służy „Kontroli procesu › Podwykonawcy" (cała firma) i zakładce
 *  „Kontrola" w karcie projektu (`singleProject`). */
export function AnalysisPanel({ data, singleProject = false }: { data: AnalysisResult; singleProject?: boolean }) {
  return (
    <>

          <div>
            <div className="section-label mb-1.5">Zrobione w oknie</div>
            <div className="flex flex-wrap items-stretch gap-2">
              {!singleProject && (
                <StatTile label="projekty z ruchem" value={`${data.verdict.projects_active ?? 0}/${data.verdict.projects_total ?? 0}`} tone={(data.verdict.projects_active ?? 0) > 0 ? "ok" : "warn"} hint="firma ruszyła" />
              )}
              {DONE.map((r) => {
                const v = data.verdict[r.key] ?? 0;
                return <StatTile key={r.key} label={r.label} value={v} tone={v > 0 ? "ok" : "default"} />;
              })}
            </div>
          </div>
          <div>
            <div className="section-label mb-1.5">Do omówienia</div>
            <div className="flex flex-wrap items-stretch gap-2">
              {RISK.map((r) => {
                const v = data.verdict[r.key] ?? 0;
                return <StatTile key={r.key} label={r.label} value={v} tone={v > 0 ? (r.key === "titles_pending" ? "warn" : "danger") : "default"} />;
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <SectionCard title={`Zrobione — dowody · ${data.proofs.length}`}>
              <ul className="space-y-2">
                {data.proofs.map((p, i) => <Entry key={i} tone="ok" prefix={PROOF_PREFIX[p.kind]} main={p.text} details={<>{singleProject ? "" : `${p.project} · `}{fmtDate(p.at)} · {p.by}</>} />)}
                {data.proofs.length === 0 && <li className="text-[12px] text-[var(--color-text-muted)]">Brak dowodów pracy w tym oknie.</li>}
              </ul>
            </SectionCard>
            <SectionCard title={`Do omówienia · ${data.attention.length}`}>
              <ul className="space-y-2">
                {data.attention.map((a, i) => <Entry key={i} tone={a.ours ? "warn" : "bad"} prefix={ATTENTION_PREFIX[a.kind]} main={a.text} details={<>{singleProject ? "" : a.project}{a.ours && <span className={TEXT_WARN}> · po naszej stronie</span>}</>} />)}
                {data.attention.length === 0 && <li className="text-[12px] text-[var(--color-text-muted)]">Nic nie wymaga uwagi.</li>}
              </ul>
            </SectionCard>
          </div>

          {!singleProject && (
          <div>
            <div className="section-label mb-1.5">Projekty firmy</div>
            <TableShell tableClassName="text-[12px]">
              <thead>
                <tr>
                  <Th>Projekt</Th><Th className="text-right">tytuły</Th><Th className="text-right">czekają na nas</Th><Th className="text-right">kafle</Th><Th className="text-right">pisma →</Th><Th className="text-right">← odp.</Th><Th className="text-right">wpisy</Th><Th className="text-right">po terminie</Th><Th className="text-right">bez ruchu</Th>
                </tr>
              </thead>
              <tbody>
                {data.projects.map((p) => (
                  <tr key={p.leadership_id} className={clsx("border-t border-[var(--color-border-muted)]", !p.active && "text-[var(--color-text-muted)]")}>
                    <td className="px-3 py-1.5">
                      <Link to={`/power-projects/led/${p.leadership_id}`} className="hover:text-[var(--color-primary)] hover:underline">{p.name}</Link>
                      {!p.active && <span className="ml-1.5 font-mono text-[10px] uppercase tracking-wider">zero ruchu</span>}
                    </td>
                    <td className="px-3 py-1.5 text-right font-mono tabular-nums">{p.titles_secured}</td>
                    <td className={clsx("px-3 py-1.5 text-right font-mono tabular-nums", p.titles_pending && TEXT_WARN)}>{p.titles_pending}</td>
                    <td className="px-3 py-1.5 text-right font-mono tabular-nums">{p.tiles_moved}</td>
                    <td className="px-3 py-1.5 text-right font-mono tabular-nums">{p.letters_out}</td>
                    <td className="px-3 py-1.5 text-right font-mono tabular-nums">{p.letters_in}</td>
                    <td className="px-3 py-1.5 text-right font-mono tabular-nums">{p.entries}</td>
                    <td className={clsx("px-3 py-1.5 text-right font-mono tabular-nums", p.letters_overdue && "text-[var(--color-primary)]")}>{p.letters_overdue}</td>
                    <td className={clsx("px-3 py-1.5 text-right font-mono tabular-nums", p.parcels_stalled && "text-[var(--color-primary)]")}>{p.parcels_stalled}</td>
                  </tr>
                ))}
              </tbody>
            </TableShell>
          </div>
          )}

          <div>
            <div className="section-label mb-1.5">Kafle — stan na początku okna i teraz</div>
            <TableShell tableClassName="text-[12px]">
              <thead>
                <tr>
{!singleProject && <Th>Projekt</Th>}<Th>Kafel</Th><Th>Strona</Th><Th>Było</Th><Th>Jest</Th><Th>Zmiany w oknie</Th><Th className="text-right">dni w stanie</Th><Th>Termin</Th>
                </tr>
              </thead>
              <tbody>
                {data.tiles.map((t, i) => (
                  <tr key={i} className="border-t border-[var(--color-border-muted)]">
                    {!singleProject && <td className="px-3 py-1.5">{t.project}</td>}
                    <td className="px-3 py-1.5 font-medium">{t.label}</td>
                    <td className="whitespace-nowrap px-3 py-1.5">{t.side ? SIDE_LABEL[t.side] : "—"}</td>
                    <td className="whitespace-nowrap px-3 py-1.5">{t.before}</td>
                    <td className="whitespace-nowrap px-3 py-1.5 font-semibold">{t.now}</td>
                    <td className="px-3 py-1.5 font-mono text-[11px]">{t.transitions.length ? t.transitions.join("; ") : <span className="text-[var(--color-text-muted)]">bez zmian</span>}</td>
                    <td className="px-3 py-1.5 text-right font-mono tabular-nums">{t.in_state_days}</td>
                    <td className="whitespace-nowrap px-3 py-1.5 font-mono text-[11px]">{fmtDate(t.due_date)}</td>
                  </tr>
                ))}
              </tbody>
            </TableShell>
          </div>

          {data.weeks.length > 0 && (
            <SectionCard title="Tygodniami — tytuły / wpisy">
              <div className="flex flex-wrap gap-4">
                {data.weeks.map((w) => (
                  <div key={w.week} className="min-w-[5rem] text-center text-[11px]">
                    <div className="flex h-16 items-end justify-center gap-1">
                      <div className="w-3 bg-emerald-600" style={{ height: `${Math.min(64, 8 * w.titles)}px` }} title={`${w.titles} tytułów`} />
                      <div className="w-3 bg-blue-700" style={{ height: `${Math.min(64, 4 * w.entries)}px` }} title={`${w.entries} wpisów`} />
                    </div>
                    <div className="mt-1 font-mono">{w.week}</div>
                    <div className="text-[var(--color-text-muted)]">{w.titles} / {w.entries}</div>
                  </div>
                ))}
              </div>
            </SectionCard>
          )}
        
    </>
  );
}
